/**
 * Executive Morning Brief · P1 · DEC-048 Consumer Page.
 *
 * The "first cup of coffee" view. Sean/Kevin land here every morning and
 * see, in reading order:
 *
 *   1. Header · today's date + one-line executive summary
 *   2. Alerts strip · anything flagged as warning
 *   3. What should we buy?          → /api/purchasing-recommendations
 *   4. What does our inventory cost?→ /api/fifo/insights
 *   5. What do we have?             → /api/pic/inventory-health/dashboard/summary
 *   6. What should we sell it for?  → /api/daily-pricing/rack/current
 *                                   + /api/customer-pricing/admin/recent-changes
 *   7. Human Intelligence           → /api/purchasing-recommendations/signals/list
 *   8. Footer · advisory disclaimer + generated timestamp + refresh
 *
 * Architectural contract (DEC-048): the Brief is a **pure consumer** —
 * every card is a projection of an existing engine's output. No business
 * logic here. Engines do the math; this page assembles.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sun, Lightbulb, Stack, Package, CurrencyDollar, ChatCircleText,
  ArrowsClockwise, WarningCircle, Info, CaretRight, TrendUp, TrendDown,
  Users, Factory, Wrench, Truck, ChartLineUp, CloudRain, Coffee, PencilLine,
  ListChecks,
} from "@phosphor-icons/react";
import ActionsMenu from "@/components/ActionsMenu";
import AddOperationalNoteDialog from "@/components/AddOperationalNoteDialog";
import RecommendationList from "@/components/RecommendationList";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const brief = {
  recommendations: () => api.get("/purchasing-recommendations"),
  fifoInsights:    () => api.get("/fifo/insights"),
  invHealth:       () => api.get("/pic/inventory-health/dashboard/summary"),
  rackCurrent:     () => api.get("/daily-pricing/rack/current"),
  recentChanges:   () => api.get("/customer-pricing/admin/recent-changes"),
  humanIntel:      () => api.get("/purchasing-recommendations/signals/list"),
  todayIntel:      () => api.get("/morning-intelligence/today"),
  opsNotes:        () => api.get("/operational-notes"),
  // Sprint 10 · 2026-08-06 — authoritative readings/reconciliation source.
  // Morning Brief used to derive the "Complete Daily Readings" blocker
  // from PIC IH (a legacy pre-Sprint-2 pipeline that Start-the-Day never
  // writes to), which always reported every product missing. The real
  // completion state lives in `/api/reconciliation/today`.
  reconToday:      () => api.get("/reconciliation/today"),
};

const PRODUCT_LABELS = {
  regular:      "Regular Gas",
  premium:      "Premium Gas",
  clear_diesel: "Clear Diesel",
  dyed_diesel:  "Dyed Diesel",
  def:          "DEF",
};

const ACTION_STYLES = {
  BUY:   "bg-amber-100 text-amber-900 border-amber-300",
  WATCH: "bg-sky-100 text-sky-900 border-sky-300",
  HOLD:  "bg-slate-100 text-slate-700 border-slate-300",
};
// DEC-049 · presentation-only vocabulary. Engine enums stay BUY/WATCH/HOLD.
const ACTION_LABEL = { BUY: "ORDER", WATCH: "WATCH", HOLD: "WAIT" };
const BAND_STYLES = {
  high:   { chip: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "High" },
  medium: { chip: "bg-amber-100 text-amber-900 border-amber-300",       label: "Medium" },
  low:    { chip: "bg-rose-100 text-rose-800 border-rose-300",          label: "Low" },
};
const TONE_STYLES = {
  red:    "bg-rose-100 text-rose-800 border-rose-200",
  amber:  "bg-amber-100 text-amber-900 border-amber-200",
  green:  "bg-emerald-100 text-emerald-800 border-emerald-200",
  unknown:"bg-slate-100 text-slate-600 border-slate-200",
};

function fmtDate(d) {
  return d.toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}
function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
function fmtAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
function fmtMoney(v, places = 2) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: places, maximumFractionDigits: places });
}
function firstName(user) {
  const name = user?.name || user?.email || "";
  const parts = name.split(/[\s@]+/).filter(Boolean);
  const first = parts[0] || "there";
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/* ------------------------------------------------------------------
   Section shell
   ------------------------------------------------------------------ */
function Section({ icon: Icon, title, question, to, children, testid }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-baseline gap-3 flex-wrap">
        <Icon size={18} className="text-slate-500 self-center" />
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500 italic">{question}</p>
        </div>
        {to ? (
          <Link to={to} className="text-xs font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
            Open workspace <CaretRight size={12} />
          </Link>
        ) : null}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------
   Morning Intelligence read-only projections · DEC-050.
   ------------------------------------------------------------------ */
const PRODUCT_LABELS_FULL = {
  regular:      "Regular Gas",
  premium:      "Premium Gas",
  clear_diesel: "Clear Diesel",
  dyed_diesel:  "Dyed Diesel",
  def:          "DEF",
};

const DIRECTION_CHIP = {
  supports_buy:        { className: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Supports order" },
  against_buy:         { className: "bg-rose-100 text-rose-800 border-rose-300",           label: "Against order" },
  supplier_constraint: { className: "bg-amber-100 text-amber-900 border-amber-300",        label: "Constraint" },
};

const WEATHER_LABEL = {
  clear: "Clear", rain: "Rain", snow: "Snow", storm: "Storm", extreme_cold: "Extreme Cold",
};

function IntelChip({ direction }) {
  const s = DIRECTION_CHIP[direction] || DIRECTION_CHIP.supports_buy;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${s.className}`}>
      {s.label}
    </span>
  );
}

function IntelSection({ icon: Icon, title, question, state, renderer, testid, briefId, onActionCreated }) {
  const hasContent = (() => {
    if (!state) return false;
    if (renderer === "entries-customer" || renderer === "entries-supplier" || renderer === "entries-market")
      return (state.entries || []).length > 0 || (state.notes || "").trim().length > 0;
    if (renderer === "notes")   return (state.notes || "").trim().length > 0;
    if (renderer === "fleet")   return true;   // fleet always renders a state
    if (renderer === "weather") return !!state.condition;
    return false;
  })();

  return (
    <Section icon={Icon} title={title} question={question}
             to="/morning-capture" testid={testid}>
      {!hasContent ? (
        <p className="text-sm text-slate-500" data-testid={`${testid}-empty`}>
          Nothing to report.
        </p>
      ) : renderer === "entries-customer" ? (
        <ul className="space-y-1.5" data-testid={`${testid}-list`}>
          {(state.entries || []).map((e, i) => (
            <li key={i} className="flex items-baseline gap-2 text-sm text-slate-800"
                data-testid={`${testid}-item-${i}`}>
              {e.customer_name ? <b className="text-slate-900">{e.customer_name}:</b> : null}
              <span className="flex-1">{e.note}</span>
              {briefId ? (
                <ActionsMenu
                  actions={[{
                    key: "schedule-followup", label: "Schedule Follow-up", icon: Users,
                    prefill: {
                      task_type: "General",
                      subject: `Follow up · ${e.customer_name || "customer"}`,
                      description: e.note,
                      customer_name: e.customer_name || undefined,
                      source_type: "morning_intelligence",
                      source_id:   briefId,
                      source_ref:  { section: "customer_intel", entry_index: i,
                                     customer_name: e.customer_name || null,
                                     customer_id:   e.customer_id   || null },
                    },
                  }]}
                  onCreated={onActionCreated}
                  dataTestidPrefix={`${testid}-actions-${i}`}
                />
              ) : null}
            </li>
          ))}
          {state.notes ? <li className="text-sm text-slate-700 italic pt-1 border-t border-slate-100">{state.notes}</li> : null}
        </ul>
      ) : renderer === "entries-supplier" ? (
        <ul className="space-y-2" data-testid={`${testid}-list`}>
          {(state.entries || []).map((e, i) => (
            <li key={i} className="text-sm text-slate-800" data-testid={`${testid}-item-${i}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                {e.supplier ? <b className="text-slate-900">{e.supplier}</b> : null}
                {e.product_code ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
                    {PRODUCT_LABELS_FULL[e.product_code] || e.product_code}
                  </span>
                ) : null}
                <IntelChip direction={e.direction} />
                {briefId ? (
                  <span className="ml-auto">
                    <ActionsMenu
                      actions={[{
                        key: "create-purchasing-task", label: "Create Purchasing Task", icon: ListChecks,
                        prefill: {
                          task_type: "Administration",
                          subject: `Follow up · supplier ${e.supplier || "—"}${e.product_code ? ` · ${PRODUCT_LABELS_FULL[e.product_code]}` : ""}`,
                          description: e.note,
                          source_type: "morning_intelligence",
                          source_id:   briefId,
                          source_ref:  { section: "supplier_intel", entry_index: i,
                                         supplier:     e.supplier     || null,
                                         product_code: e.product_code || null,
                                         direction:    e.direction    || null },
                        },
                      }]}
                      onCreated={onActionCreated}
                      dataTestidPrefix={`${testid}-actions-${i}`}
                    />
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5">{e.note}</p>
            </li>
          ))}
          {state.notes ? <li className="text-sm text-slate-700 italic pt-1 border-t border-slate-100">{state.notes}</li> : null}
        </ul>
      ) : renderer === "entries-market" ? (
        <ul className="space-y-2" data-testid={`${testid}-list`}>
          {(state.entries || []).map((e, i) => (
            <li key={i} className="text-sm text-slate-800" data-testid={`${testid}-item-${i}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                {e.product_code ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
                    {PRODUCT_LABELS_FULL[e.product_code] || e.product_code}
                  </span>
                ) : null}
                <IntelChip direction={e.direction} />
              </div>
              <p className="mt-0.5">{e.note}</p>
            </li>
          ))}
          {state.notes ? <li className="text-sm text-slate-700 italic pt-1 border-t border-slate-100">{state.notes}</li> : null}
        </ul>
      ) : renderer === "notes" ? (
        <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid={`${testid}-notes`}>
          {state.notes}
        </p>
      ) : renderer === "fleet" ? (
        <div className="space-y-1" data-testid={`${testid}-body`}>
          <div className="flex items-center gap-3">
            <p className="text-sm flex-1">
              {state?.has_issue === true ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-rose-500" />
                  <b className="text-slate-900">Fleet or staffing issue reported.</b>
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="text-slate-800">All trucks and drivers available.</span>
                </span>
              )}
            </p>
            {briefId && state?.has_issue === true ? (
              <ActionsMenu
                actions={[{
                  key: "create-maintenance-task", label: "Create Maintenance Task", icon: Wrench,
                  prefill: {
                    task_type: "Fleet",
                    subject: `Fleet issue · ${new Date().toLocaleDateString()}`,
                    description: state.notes || "Fleet or staffing issue reported this morning.",
                    priority: "High",
                    source_type: "morning_intelligence",
                    source_id:   briefId,
                    source_ref:  { section: "fleet" },
                  },
                }]}
                onCreated={onActionCreated}
                dataTestidPrefix={`${testid}-actions`}
              />
            ) : null}
          </div>
          {state?.notes ? <p className="text-sm text-slate-700 mt-1">{state.notes}</p> : null}
        </div>
      ) : renderer === "weather" ? (
        <div className="space-y-1" data-testid={`${testid}-body`}>
          <p className="text-sm">
            <span className="inline-flex items-center gap-2 text-slate-900 font-semibold">
              {WEATHER_LABEL[state.condition] || state.condition}
            </span>
          </p>
          {state.notes ? <p className="text-sm text-slate-700">{state.notes}</p> : null}
        </div>
      ) : null}
    </Section>
  );
}

/* ------------------------------------------------------------------
   Root
   ------------------------------------------------------------------ */
export default function MorningBrief() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const [recs, setRecs] = useState(null);
  const [insights, setInsights] = useState(null);
  const [invHealth, setInvHealth] = useState(null);
  const [rack, setRack] = useState(null);
  const [recent, setRecent] = useState(null);
  const [hiNotes, setHiNotes] = useState(null);
  const [intel, setIntel] = useState(null);  // today's Morning Intelligence Capture
  const [opsNotes, setOpsNotes] = useState(null);
  const [reconToday, setReconToday] = useState(null);  // authoritative Start-the-Day recon
  const [noteOpen, setNoteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Every engine responds independently — one failure shouldn't blank
    // the whole Brief.
    const results = await Promise.allSettled([
      brief.recommendations(), brief.fifoInsights(), brief.invHealth(),
      brief.rackCurrent(),     brief.recentChanges(), brief.humanIntel(),
      brief.todayIntel(),      brief.opsNotes(),      brief.reconToday(),
    ]);
    const setters = [setRecs, setInsights, setInvHealth, setRack, setRecent,
                     setHiNotes, setIntel, setOpsNotes, setReconToday];
    let anyFailed = false;
    results.forEach((r, i) => {
      if (r.status === "fulfilled") setters[i](r.value.data);
      else { setters[i](null); anyFailed = true; }
    });
    setRefreshedAt(new Date());
    setLoading(false);
    if (anyFailed) setError("Some engines could not be reached. The Brief is showing what it has.");
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5 minutes while the tab is visible.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) load();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  const today = new Date();

  // ----- Header summary counts -----------------------------------------
  const summary = useMemo(() => {
    const s = { BUY: 0, WATCH: 0, HOLD: 0 };
    (recs?.items || []).forEach((it) => { s[it.action] = (s[it.action] || 0) + 1; });
    return s;
  }, [recs]);

  const portfolioValue = useMemo(() => {
    const pv = (insights?.items || []).find((it) => it.code === "FIFO_PORTFOLIO_VALUATION");
    return pv?.payload?.portfolio_value_cad || null;
  }, [insights]);

  const alerts = useMemo(() => {
    const list = [];
    (insights?.items || []).filter((it) => it.severity === "warning")
      .forEach((it) => list.push({ kind: "fifo", code: it.code, narrative: it.narrative, product_code: it.product_code }));
    (hiNotes?.items || []).filter((n) => n.kind === "supplier_constraint" && n.active)
      .forEach((n) => list.push({ kind: "hi", code: "SUPPLIER_CONSTRAINT", narrative: n.text, product_code: n.product_code, posted_by: n.posted_by }));
    return list;
  }, [insights, hiNotes]);

  const alertsCount = alerts.length;

  // ----- Executive summary · Sprint 7 Morning Brief redesign -----------
  // Answers: "Is the business healthy?" in one line, and lists up to 5
  // prioritised actions blockers-first. Presentation-only synthesis of
  // existing engine outputs — no business logic here (LAW-007).
  const priorities = useMemo(() => {
    const list = [];

    // Tier 1 · Operational blockers (day cannot proceed correctly).
    //
    // Sprint 10 · 2026-08-06 — the authoritative "are today's readings
    // complete?" signal lives on `/api/reconciliation/today`. The old
    // PIC IH dashboard reports missing_products for every product
    // because Start-the-Day never writes to `pic_ih_readings`, so we
    // ignore it here and derive missing products from
    // `reconToday.items[].missing_inputs` instead.
    const readingsMissingProducts = (reconToday?.items || [])
      .filter((it) => (it.missing_inputs || []).some(
        (k) => k === "throughput_yesterday" || k === "physical_today" || k === "prior_physical"
      ))
      .map((it) => it.product_code);
    if (reconToday?.has_missing_inputs && readingsMissingProducts.length) {
      list.push({
        tier: 1,
        code: "READINGS_MISSING",
        title: "Complete Daily Readings",
        why: `Reconciliation cannot begin until readings are recorded for ${readingsMissingProducts.map((c) => PRODUCT_LABELS[c] || c).join(", ")}.`,
        to: "/start-the-day/daily-readings",
        actionLabel: "Record now",
      });
    }
    // Sprint 10 · 2026-08-06 — rack API returns items with fields nested
    // under `current.{effective_at, price_per_litre, ...}`. Reading them
    // at the top level (as the original synthesis did) makes every check
    // return false → both blockers fire whenever items exist. `today`
    // for the "stale" comparison is the calendar day the Brief renders.
    const _todayStr = new Date().toISOString().slice(0, 10);
    const _isToday = (iso) => typeof iso === "string" && iso.slice(0, 10) === _todayStr;
    const rackItems = rack?.items || [];
    const rackHasAny = rackItems.some((r) => r?.current?.effective_at);
    const rackHasToday = rackItems.some((r) => _isToday(r?.current?.effective_at));
    const rackStale = rackHasAny && !rackHasToday;
    if (!rackHasAny) {
      list.push({
        tier: 1,
        code: "RACK_UNPOSTED",
        title: "Post today's Supplier Rack Prices",
        why: "Selling Prices cannot be generated until today's supplier rack prices have been posted.",
        to: "/price-notifications",
        actionLabel: "Post prices",
      });
    } else if (rackStale) {
      list.push({
        tier: 2,
        code: "RACK_STALE",
        title: "Refresh today's Supplier Rack Prices",
        why: "One or more products still carry yesterday's supplier price. Review before generating today's Selling Prices.",
        to: "/price-notifications",
        actionLabel: "Review",
      });
    }

    // Tier 2 · Time-critical purchasing actions
    (recs?.items || [])
      .filter((it) => it.action === "BUY")
      .slice(0, 3)
      .forEach((it) =>
        list.push({
          tier: 2,
          code: "BUY_" + (it.product_code || "").toUpperCase(),
          title: `Order ${PRODUCT_LABELS[it.product_code] || it.product_code}`,
          why: it.narrative || it.summary || "Purchasing engine flagged a buying opportunity.",
          to: "/purchasing",
          actionLabel: "Review order",
        })
      );

    // Tier 3 · Warning-severity inventory / cost insights
    (insights?.items || [])
      .filter((it) => it.severity === "warning" && it.code !== "FIFO_PORTFOLIO_VALUATION")
      .slice(0, 2)
      .forEach((it) =>
        list.push({
          tier: 3,
          code: it.code,
          title: PRODUCT_LABELS[it.product_code]
            ? `Review ${PRODUCT_LABELS[it.product_code]} inventory`
            : "Review inventory position",
          why: it.narrative || "Inventory Health engine flagged a warning.",
          to: "/fifo",
          actionLabel: "View FIFO",
        })
      );

    // Tier 3 · Active human-intel supplier constraints
    (hiNotes?.items || [])
      .filter((n) => n.kind === "supplier_constraint" && n.active)
      .slice(0, 1)
      .forEach((n) =>
        list.push({
          tier: 3,
          code: "SUPPLIER_CONSTRAINT",
          title: "Supplier constraint noted",
          why: n.text || "A supplier availability constraint is currently active.",
          to: "/purchasing",
          actionLabel: "Review",
        })
      );

    return list.sort((a, b) => a.tier - b.tier).slice(0, 5);
  }, [rack, invHealth, recs, insights, hiNotes, reconToday]);

  const status = useMemo(() => {
    const hasBlocker = priorities.some((p) => p.tier === 1);
    const hasAttention = priorities.some((p) => p.tier === 2);
    if (hasBlocker) {
      return {
        level: "red",
        label: "Operational Risk",
        reason: priorities.find((p) => p.tier === 1)?.why || "Blocker present.",
      };
    }
    if (hasAttention || alertsCount > 0) {
      return {
        level: "amber",
        label: "Attention Required",
        reason: "Time-critical actions are pending today.",
      };
    }
    return {
      level: "green",
      label: "Operations Normal",
      reason: "All critical operational requirements have been satisfied.",
    };
  }, [priorities, alertsCount]);

  const showAlerts = alertsCount > 0;
  const showFifoCosts = insights && (insights.items || []).some((it) => it.code !== "FIFO_PORTFOLIO_VALUATION");
  // Sprint 10 · 2026-08-06 — anchor the inventory panel to authoritative
  // Start-the-Day recon signals. The legacy PIC IH pipeline is not
  // populated by Start-the-Day, so its missing_products / by_product
  // reads are ignored on the Brief.
  const reconMissingProducts = (reconToday?.items || [])
    .filter((it) => (it.missing_inputs || []).length > 0)
    .map((it) => it.product_code);
  const showInvHealth = invHealth && (
    (invHealth.reconciliation?.by_product?.length || 0) > 0
    || reconMissingProducts.length > 0
    || (invHealth.open_investigations?.length || 0) > 0
  );
  const showRecentPricing = (recent?.items?.length || 0) > 0;
  const showPricing = !!rack;
  const hasIntelSection = intel && intel.sections
    && Object.values(intel.sections).some((s) => s && (s.entries?.length || (s.text && String(s.text).trim())));
  const showIntel = intel?.status === "prepared" && hasIntelSection;
  const STATUS_DOT = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red:   "bg-rose-500",
  };
  const STATUS_TONE = {
    green: "text-emerald-700",
    amber: "text-amber-800",
    red:   "text-rose-700",
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="brief-workspace">
      {/* --- Header ------------------------------------------------- */}
      <header className="rounded-lg border border-slate-200 bg-gradient-to-br from-amber-50 via-white to-white p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <Sun size={20} className="text-amber-600" weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Executive Morning Brief</div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="brief-greeting">
              Good morning, {firstName(user)}.
            </h1>
            <p className="text-sm text-slate-600 mt-0.5" data-testid="brief-date">{fmtDate(today)}</p>
            {/* Morning Intelligence prepared status · retained read-only
                signal so operators see when today's Brief is grounded in
                a prior capture. The workflow no longer *requires* it —
                per PO directive 2026-08-01, Start the Day is a five-step
                workflow and operational context is captured via optional
                Operational Notes (below). */}
            {intel?.status === "prepared" ? (
              <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1" data-testid="brief-intel-prepared">
                <Coffee size={12} weight="fill" />
                Morning Intelligence prepared by <b className="ml-0.5">{intel.prepared_by_name}</b>
                {intel.prepared_at ? <> · {new Date(intel.prepared_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</> : null}
              </p>
            ) : null}
            <p className="text-sm text-slate-800 mt-3 flex items-center gap-2" data-testid="brief-summary-line">
              <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_DOT[status.level]}`} />
              <b data-testid="brief-status-label" className={STATUS_TONE[status.level]}>{status.label}.</b>
              <span className="text-slate-600" data-testid="brief-status-reason">{status.reason}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setNoteOpen(true)} data-testid="brief-add-note"
                    className="text-xs font-medium px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
              <PencilLine size={12} /> Add operational note
            </button>
            <button onClick={load} data-testid="brief-refresh"
                    className="text-xs font-medium px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
              <ArrowsClockwise size={12} /> Refresh
            </button>
          </div>
        </div>
        {error ? (
          <p className="mt-3 text-xs text-amber-800 bg-amber-50 rounded px-3 py-1.5 inline-block" data-testid="brief-partial-error">{error}</p>
        ) : null}
      </header>

      {/* --- Today's Priorities · Sprint 7 executive brief ------------
          Answers: "What requires my attention first?" Max 5, blockers
          first. Hidden entirely when the operator has no priorities —
          the ARC-013 rule that empty sections must not shout. */}
      {priorities.length > 0 ? (
        <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="brief-priorities">
          <div className="px-5 py-3 border-b border-slate-100 flex items-baseline gap-3">
            <ListChecks size={18} className="text-slate-500" weight="regular" />
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-slate-900">Today's Priorities</h2>
              <p className="text-xs text-slate-500 italic">What requires my attention first?</p>
            </div>
            <span className="text-xs text-slate-500" data-testid="brief-priorities-count">{priorities.length}</span>
          </div>
          <ol className="divide-y divide-slate-100">
            {priorities.map((p, i) => {
              const tier = p.tier === 1
                ? { chip: "bg-rose-100 text-rose-800 border-rose-300",     label: "Blocker" }
                : p.tier === 2
                ? { chip: "bg-amber-100 text-amber-900 border-amber-300",  label: "Today" }
                : { chip: "bg-sky-100 text-sky-900 border-sky-300",        label: "Watch" };
              return (
                <li key={p.code} className="px-5 py-3 flex items-start gap-3" data-testid={`brief-priority-${i}`}>
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-700 tabular-nums shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold text-slate-900">{p.title}</div>
                      <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tier.chip}`}>
                        {tier.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">{p.why}</p>
                  </div>
                  <Link to={p.to} className="text-xs font-semibold text-slate-800 hover:text-slate-900 inline-flex items-center gap-0.5 shrink-0"
                        data-testid={`brief-priority-${i}-action`}>
                    {p.actionLabel} <CaretRight size={10} weight="bold" />
                  </Link>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}

      {/* --- Alerts (hidden when zero) ------------------------------- */}
      {showAlerts ? (
      <section className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="brief-alerts">
        <div className="px-5 py-3 border-b border-slate-100 flex items-baseline gap-3">
          <WarningCircle size={18} className={alertsCount > 0 ? "text-rose-500" : "text-slate-400"} weight={alertsCount > 0 ? "fill" : "regular"} />
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-slate-900">Alerts</h2>
            <p className="text-xs text-slate-500 italic">Anything the engines think you should see first.</p>
          </div>
          <span className="text-xs text-slate-500" data-testid="brief-alerts-count">{alertsCount}</span>
        </div>
        <div className="p-5">
          {loading && !recs ? <p className="text-sm text-slate-500">Loading…</p> :
           alertsCount === 0 ? (
            <p className="text-sm text-slate-600" data-testid="brief-alerts-empty">
              No alerts. All engines quiet.
            </p>
          ) : (
            <ul className="space-y-2">
              {alerts.map((a, i) => (
                <li key={i} data-testid={`brief-alert-${i}`}
                    className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50/60 px-3 py-2">
                  <WarningCircle size={14} className="text-rose-600 mt-0.5 shrink-0" weight="fill" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-rose-700 font-mono">{a.code}</div>
                    <p className="text-sm text-slate-900">{a.narrative}</p>
                    {a.posted_by ? <p className="text-[11px] text-slate-500 mt-0.5">Posted by {a.posted_by}</p> : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      ) : null}

      {/* --- Morning Intelligence Capture (only when prepared today) - */}
      {showIntel ? (
      <>
      <IntelSection
        icon={Users} testid="brief-intel-customer"
        title="Customer Intelligence"
        question="Anything about customers today?"
        state={intel?.sections?.customer_intel}
        renderer="entries-customer"
        briefId={intel?.id}
        onActionCreated={() => toast.success("Task created and linked to the Morning Brief")}
      />
      <IntelSection
        icon={Factory} testid="brief-intel-supplier"
        title="Supplier Intelligence"
        question="Anything affecting suppliers today?"
        state={intel?.sections?.supplier_intel}
        renderer="entries-supplier"
        briefId={intel?.id}
        onActionCreated={() => toast.success("Task created and linked to the Morning Brief")}
      />
      <IntelSection
        icon={Wrench} testid="brief-intel-operations"
        title="Operations"
        question="Anything affecting today's operations?"
        state={intel?.sections?.operations}
        renderer="notes"
      />
      <IntelSection
        icon={Truck} testid="brief-intel-fleet"
        title="Fleet"
        question="Any fleet or staffing issues today?"
        state={intel?.sections?.fleet}
        renderer="fleet"
        briefId={intel?.id}
        onActionCreated={() => toast.success("Maintenance task created and linked to the Morning Brief")}
      />
      <IntelSection
        icon={ChartLineUp} testid="brief-intel-market"
        title="Market Intelligence"
        question="Anything affecting fuel markets today?"
        state={intel?.sections?.market}
        renderer="entries-market"
      />
      <IntelSection
        icon={CloudRain} testid="brief-intel-weather"
        title="Weather & External Factors"
        question="Any weather or road conditions worth noting?"
        state={intel?.sections?.weather}
        renderer="weather"
      />
      </>
      ) : null}

      {/* --- Purchasing Recommendations (RecommendationList self-hides when empty) - */}
      <RecommendationList title="Today's Recommendations" testidPrefix="brief-recs" />

      {/* --- Fuel Inventory Cost (hidden when no cost insights) ------ */}
      {showFifoCosts ? (
      <Section icon={Stack} title="What does our fuel inventory actually cost?"
               question="FIFO layers · landed cost · fuel inventory valuation."
               to="/fifo" testid="brief-cost">
        {loading && !insights ? <p className="text-sm text-slate-500">Loading…</p> :
         !insights ? <p className="text-sm text-slate-500">No cost intelligence available.</p> : (
          <div className="space-y-3">
            <div className="rounded-md bg-slate-900 text-white p-4 flex items-baseline justify-between gap-4">
              <div>
                <div className="text-xs text-slate-300 uppercase tracking-wide">FIFO Fuel Inventory Value</div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  Gasoline and diesel only. Lubricants, DEF, and other inventory categories are excluded.
                </div>
              </div>
              <div className="text-2xl font-semibold tabular-nums shrink-0" data-testid="brief-cost-portfolio">
                ${fmtMoney(portfolioValue)} <span className="text-sm text-slate-400 font-normal">CAD</span>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {(insights.items || [])
                .filter((it) => it.code !== "FIFO_PORTFOLIO_VALUATION")
                .slice(0, 6)
                .map((it, i) => (
                <li key={i} className="py-2 flex items-start gap-3"
                    data-testid={`brief-cost-insight-${i}`}>
                  {it.severity === "warning"
                    ? <WarningCircle size={14} className="text-rose-600 mt-0.5 shrink-0" weight="fill" />
                    : <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-mono">{it.code}</div>
                    <p className="text-sm text-slate-800">{it.narrative}</p>
                  </div>
                </li>
              ))}
            </ul>
            {(insights.items || []).length <= 1 ? (
              <p className="text-xs text-slate-500 italic">FIFO is quiet — no cost changes or ageing layers to flag.</p>
            ) : null}
          </div>
        )}
      </Section>
      ) : null}

      {/* --- Inventory position (hidden when no products / no readings issues) --- */}
      {showInvHealth ? (
      <Section icon={Package} title="What do we have?"
               question="Inventory position across products."
               to="/purchasing" testid="brief-inventory">
        {loading && !invHealth ? <p className="text-sm text-slate-500">Loading…</p> :
         !invHealth ? null : (
          <div className="space-y-3">
            {invHealth.reconciliation?.by_product?.length ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {invHealth.reconciliation.by_product.map((p) => {
                  const tone = TONE_STYLES[p.tone] || TONE_STYLES.unknown;
                  return (
                    <div key={p.product_code} className={`rounded border px-3 py-2 ${tone}`}
                         data-testid={`brief-inv-${p.product_code}`}>
                      <div className="text-xs font-medium">{PRODUCT_LABELS[p.product_code] || p.product_code}</div>
                      <div className="text-[10px] mt-0.5 uppercase tracking-wide">{p.tone || "—"}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {reconMissingProducts.length ? (
              <p className="text-xs text-slate-600" data-testid="brief-inv-missing">
                Missing readings today for{" "}
                <b>{reconMissingProducts.map((c) => PRODUCT_LABELS[c] || c).join(", ")}</b>
                {" · "}<Link to="/start-the-day/daily-readings" className="text-slate-800 underline">record now</Link>.
              </p>
            ) : null}
            {invHealth.highest_open_tone ? (
              <p className="text-xs text-slate-600">
                Highest open tone: <b className="uppercase">{invHealth.highest_open_tone}</b> ·{" "}
                {(invHealth.open_investigations || []).length} open investigation(s).
              </p>
            ) : null}
            <p className="text-[11px] text-slate-500">
              Snapshot as of {fmtDT(invHealth.as_of)}
            </p>
          </div>
        )}
      </Section>
      ) : null}

      {/* --- Selling Prices / Supplier Rack Prices (hidden when engine unavailable) --- */}
      {showPricing ? (
      <Section icon={CurrencyDollar} title="What should we sell it for?"
               question="Today's Supplier Rack Prices + recent pricing activity."
               to="/pricing" testid="brief-pricing">
        {loading && !rack ? <p className="text-sm text-slate-500">Loading…</p> :
         !rack ? <p className="text-sm text-slate-500">Daily Pricing engine unavailable.</p> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="brief-rack-grid">
              {(rack.items || []).map((r) => {
                // API contract: rack fields are nested under `current`
                // (see `/api/daily-pricing/rack/current` — Sprint 10 fix).
                const cur = r?.current || {};
                const price = cur.price_per_litre;
                const effectiveAt = cur.effective_at;
                const _today = new Date().toISOString().slice(0, 10);
                const stale = effectiveAt && effectiveAt.slice(0, 10) !== _today;
                return (
                  <div key={r.product_code} className={`rounded border px-3 py-2 ${stale ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
                       data-testid={`brief-rack-${r.product_code}`}>
                    <div className="text-xs text-slate-500">{PRODUCT_LABELS[r.product_code] || r.product_code}</div>
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">
                      {price ? `$${price}` : "—"}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {effectiveAt ? fmtAgo(effectiveAt) : "no post"}
                      {stale ? <span className="ml-1 text-amber-700 font-medium">· stale</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>
            {recent?.items?.length ? (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Recent pricing activity</div>
                <ul className="divide-y divide-slate-100">
                  {recent.items.slice(0, 4).map((c, i) => (
                    <li key={i} className="py-1.5 flex items-baseline gap-2 text-xs"
                        data-testid={`brief-pricing-change-${i}`}>
                      {c.direction === "up" ? <TrendUp size={12} className="text-rose-600" /> :
                       c.direction === "down" ? <TrendDown size={12} className="text-emerald-600" /> :
                       <Info size={12} className="text-slate-400" />}
                      <span className="text-slate-700 flex-1 truncate">{c.summary || c.description || `${c.product_code || "—"} · ${c.change_type || ""}`}</span>
                      <span className="text-slate-500">{fmtAgo(c.effective_at || c.changed_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </Section>
      ) : null}

      {/* --- Human Intelligence (hidden when no active notes) -------- */}
      {(hiNotes?.items || []).length > 0 ? (
      <Section icon={ChatCircleText} title="Human Intelligence"
               question="Context the engines can't see on their own."
               to="/purchasing" testid="brief-hi">
        <ul className="divide-y divide-slate-100">
          {(hiNotes.items || []).slice(0, 5).map((n) => (
            <li key={n.id} className="py-2" data-testid={`brief-hi-${n.id}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-800">
                  {PRODUCT_LABELS[n.product_code] || n.product_code}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
                  {n.kind === "supplier_constraint" ? "Supplier constraint" : n.kind.replace(/_/g, " ")}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-600">
                  {n.weight}
                </span>
              </div>
              <p className="text-sm text-slate-800 mt-1">{n.text}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{n.posted_by} · {fmtDT(n.posted_at)}</p>
            </li>
          ))}
        </ul>
      </Section>
      ) : null}

      {/* --- General Notes from Morning Intelligence · DEC-050 -------- */}
      {intel?.sections?.general_notes?.text ? (
        <Section icon={PencilLine} title="General Notes"
                 question="Free-form context captured this morning."
                 to="/morning-capture" testid="brief-intel-general">
          <p className="text-sm text-slate-800 whitespace-pre-wrap"
             data-testid="brief-intel-general-text">
            {intel.sections.general_notes.text}
          </p>
        </Section>
      ) : null}

      {/* --- Operator Notes · PO 2026-08-01 ---------------------------- */}
      {(opsNotes?.notes?.length || 0) > 0 ? (
        <section
          className="rounded-lg border border-slate-200 bg-white overflow-hidden"
          data-testid="brief-operator-notes"
        >
          <div className="px-5 py-3 border-b border-slate-100 flex items-baseline gap-3">
            <PencilLine size={16} className="text-slate-500" weight="fill" />
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-slate-900">Operator Notes</h2>
              <p className="text-xs text-slate-500 italic">
                Things affecting today's operations that the system cannot infer automatically.
              </p>
            </div>
            <button
              onClick={() => setNoteOpen(true)}
              data-testid="brief-operator-notes-add"
              className="text-xs font-medium text-slate-700 hover:text-slate-900"
            >
              + Add note
            </button>
          </div>
          <ul className="divide-y divide-slate-100">
            {opsNotes.notes.map((n, i) => (
              <li key={n.id || i} className="px-5 py-3" data-testid={`brief-operator-note-${i}`}>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
                <p className="text-[11px] text-slate-500 mt-1">
                  {n.author_name || n.author_email || "Operator"} ·{" "}
                  {n.created_at ? new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <AddOperationalNoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        onSaved={load}
        testidPrefix="brief-add-note"
      />

      {/* --- Footer ------------------------------------------------- */}
      <footer className="text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2 pt-2"
              data-testid="brief-footer">
        <p className="max-w-2xl">
          Advisory only. Engines advise; the Owner decides. Every card on this page is a
          projection of a dedicated engine — no business logic lives here.
        </p>
        <p data-testid="brief-refreshed-at">
          Refreshed {refreshedAt ? fmtAgo(refreshedAt.toISOString()) : "—"}
        </p>
      </footer>
    </div>
  );
}
